import { UriIssue } from '../../../types';
import {
  isBlockedProtocol,
  isHrefStructurallyValid,
} from '../../../utils/uri-utils/uri-utils';

// Anchor href extraction (fast, non-DOM)
const HREF_REGEX =
  /<a\b[^>]*?href\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))[^>]*>/gi;

/**
 * Inspect rich-text for <a> elements and validate their href attributes.
 * Returns the first issue encountered or undefined if all links are acceptable.
 */
export function findFirstUriIssueRichText(
  richText: string,
  allowedProtocols?: string[],
): UriIssue | undefined {
  // Fast pre-filter: avoid regex work if there's no '<a' at all. Lowercase once for case-insensitive check.
  // (We choose includes("<a") + toLowerCase over a /<a/i regex to avoid extra regex allocation per call.)
  if (!richText.toLowerCase().includes('<a')) return undefined;

  // Because HREF_REGEX is global (/g), its internal state (lastIndex) persists between calls.
  // Reset to ensure we always start scanning from the beginning of this richText input.
  HREF_REGEX.lastIndex = 0;

  // Execute the global regex in a loop. Each match returns up to three capture groups:
  //  m[1] = double-quoted href value ( "..." )
  //  m[2] = single-quoted href value ( '...' )
  //  m[3] = unquoted token (terminated by whitespace or '>')
  // We normalise by picking the first non-empty capture.
  for (let m = HREF_REGEX.exec(richText); m; m = HREF_REGEX.exec(richText)) {
    const href = m[1] || m[2] || m[3] || '';

    // 1. Empty attribute value: <a href=""> or malformed capture -> error
    if (!href) return { reason: 'emptyHref', subKey: 'enforceUriProtocols' };

    // 2. Protocol blocked by policy (e.g. javascript:, ftp:, etc.)
    if (isBlockedProtocol(href, allowedProtocols))
      return { reason: 'blockedProtocol', href, subKey: 'enforceUriProtocols' };

    // 3. Fails structural validation (bad absolute URL or malformed scheme usage)
    if (!isHrefStructurallyValid(href, allowedProtocols))
      return { reason: 'invalidHref', href, subKey: 'enforceUriProtocols' };
  }

  // No issues found in any anchor
  return undefined;
}
